/**
 * settings/app-urls.ts
 * desc: client-side route paths, plus two external link templates
 * note: `:param` / `:param?` placeholders are filled with interpolateAppUrl(); `as const` keeps every path literal, so RouteParams<typeof appUrls.X> knows the params
 */
export default {
    ROOT: '/',
    AI: '/ai',
    AI_INTENT_MODELS: '/ai/intent-models',
    AI_INTENT_MODELS_COMPARE: '/ai/intent-models/:id/compare/:compareId?',
    AI_INTENT_MODELS_CREATE: '/ai/intent-models/:id?/create',
    AI_INTENT_MODELS_DETAIL_READ: '/ai/intent-models/:id',
    AI_INTENT_MODELS_DETAIL_WRITE: '/ai/intent-models/:id/edit',
    AI_LABELING: '/ai/labeling',
    AI_LABELING_SETUP: '/ai/labeling/setup',
    AI_LABELING_UPLOAD: '/ai/labeling/upload',
    ANALYTICS: '/analytics',
    ANALYTICS_TRIPS: '/analytics/trips',
    ANALYTICS_TRIPS_DETAIL_READ: '/analytics/trips/:id',
    COMPONENTS: '/components',
    COMPONENTS_CONNECTIONS: '/components/connections',
    COMPONENTS_CONNECTIONS_CREATE: '/components/connections/:id/create',
    COMPONENTS_CONNECTIONS_DETAIL_READ: '/components/connections/:id',
    COMPONENTS_CONNECTIONS_DETAIL_WRITE: '/components/connections/:id/edit',
    COMPONENTS_CONNECTION_TYPES: '/components/connection-types',
    COMPONENTS_CONNECTION_TYPES_CREATE: '/components/connection-types/create',
    COMPONENTS_CONNECTION_TYPES_DETAIL_READ: '/components/connection-types/:id',
    COMPONENTS_CONNECTION_TYPES_DETAIL_WRITE: '/components/connection-types/:id/edit',
    COMPONENTS_ENTITIES: '/components/entities',
    COMPONENTS_INTENTS: '/components/intents',
    COMPONENTS_INTENTS_CREATE: '/components/intents/create',
    COMPONENTS_INTENTS_DETAIL_READ: '/components/intents/:id',
    COMPONENTS_INTENTS_DETAIL_WRITE: '/components/intents/:id/edit',
    COMPONENTS_REVIEW_QUEUES: '/components/review-queues',
    COMPONENTS_REVIEW_QUEUES_CREATE: '/components/review-queues/create',
    COMPONENTS_REVIEW_QUEUES_DETAIL_READ: '/components/review-queues/:id',
    COMPONENTS_REVIEW_QUEUES_DETAIL_WRITE: '/components/review-queues/:id/edit',
    COMPONENTS_STEP_FACTORIES: '/components/step-factories',
    COMPONENTS_STEP_FACTORIES_CREATE: '/components/step-factories/create',
    COMPONENTS_STEP_FACTORIES_DETAIL_READ: '/components/step-factories/:id',
    COMPONENTS_STEP_FACTORIES_DETAIL_WRITE: '/components/step-factories/:id/edit',
    COMPONENTS_STEP_FACTORIES_VERSION_CREATE: '/components/step-factories/:id/versions/create',
    COMPONENTS_STEP_FACTORIES_VERSION_DETAIL_READ: '/components/step-factories/:id/versions/:versionId',
    COMPONENTS_STEP_FACTORIES_VERSION_DETAIL_WRITE: '/components/step-factories/:id/versions/:versionId/edit',
    COMPONENTS_ORDER_SCHEMAS: '/components/order-schemas',
    COMPONENTS_ORDER_SCHEMAS_CREATE: '/components/order-schemas/create',
    COMPONENTS_ORDER_SCHEMAS_DETAIL_READ: '/components/order-schemas/:id',
    COMPONENTS_ORDER_SCHEMAS_DETAIL_WRITE: '/components/order-schemas/:id/edit',
    CONSTANTS: '/custom-data/constants',
    CONSTANTS_CREATE: '/custom-data/constants/create',
    CONSTANTS_DETAIL_READ: '/custom-data/constants/:id',
    CONSTANTS_DETAIL_WRITE: '/custom-data/constants/:id/edit',
    COLLECTIONS: '/custom-data/collections',
    COLLECTIONS_CREATE: '/custom-data/collections/create',
    COLLECTIONS_DETAIL_READ: '/custom-data/collections/:id',
    COLLECTIONS_DETAIL_WRITE: '/custom-data/collections/:id/edit',
    CUSTOM_DATA: '/custom-data',
    DISCOVER: '/discover',
    DISCOVER_APPS_TOPIC_DISCOVERY: '/labs/topic-discovery',
    ENVIRONMENTS: '/environments',
    ENVIRONMENTS_CREATE: '/environments/create',
    ENVIRONMENTS_DETAIL_READ: '/environments/:id',
    ENVIRONMENTS_DETAIL_WRITE: '/environments/:id/edit',
    ENVIRONMENT_SCHEMA: '/environment-schema',
    FILE_STORAGE: '/custom-data/file-storage',
    FRONT_PLUGIN: '/front-plugin',
    HOME: '/home',
    JOURNEYS: '/journeys',
    JOURNEYS_DETAIL_READ: '/journeys/:id',
    JOURNEYS_DETAIL_WRITE: '/journeys/:id/edit',
    LABS: '/labs',
    ORDERS: '/orders',
    ORDERS_DETAIL_READ: '/orders/:orderId',
    ORDER_REVIEWS_LIST: '/order-queue/:queueId',
    ORDER_REVIEWS_DETAIL: '/order-queue/:queueId/review/:itemId',
    REVIEWS: '/reviews',
    REVIEWS_DETAIL: '/reviews/:id',
    RULES: '/components/rules',
    RULES_CREATE: '/components/rules/create',
    RULES_DETAIL_READ: '/components/rules/:id',
    RULES_DETAIL_WRITE: '/components/rules/:id/edit',
    RULE_SETS: '/components/rule-sets',
    RULE_SETS_CREATE: '/components/rule-sets/create',
    RULE_SETS_DETAIL_READ: '/components/rule-sets/:id',
    RULE_SETS_DETAIL_WRITE: '/components/rule-sets/:id/edit',
    RULE_SET_EVALUATIONS_DETAIL_READ: '/rule-set-evaluations/:id',
    SETTINGS: '/settings',
    SETTINGS_GDPR: '/settings/gdpr',
    SETTINGS_USERS: '/settings/users',
    SETTINGS_USERS_CREATE: '/settings/users/create',
    SETTINGS_USERS_DETAIL_READ: '/settings/users/:id',
    SETTINGS_USERS_DETAIL_WRITE: '/settings/users/:id/edit',
    TESTING: '/testing',
    TESTING_WEBHOOK: '/testing/webhook',
    TESTING_RULE_SET: '/testing/rule-set',
    TRIP_LOGS: "https://management-${baseUrlPart}.ymeadows.com/logs/app/discover#/view/${logId}?_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:'${from}',to:'${to}'))&_a=(columns:!(message,container.image.name,app.log.message_obj.trip-id),filters:!(('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'filebeat-*',key:kubernetes.container.name,negate:!f,params:(query:user-container),type:phrase),query:(match_phrase:(kubernetes.container.name:user-container))),('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'filebeat-*',key:app.log.message_obj.trip-id,negate:!f,params:(query:'${tripId}'),type:phrase),query:(match_phrase:(app.log.message_obj.trip-id:'${tripId}'))),('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'filebeat-*',key:kubernetes.namespace,negate:!f,params:(query:${client}),type:phrase),query:(match_phrase:(kubernetes.namespace:${client}))),('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'filebeat-*',key:'kubernetes.labels.tierzero_ymeadows_com/image-name',negate:!f,params:(query:'${imageName}'),type:phrase),query:(match_phrase:('kubernetes.labels.tierzero_ymeadows_com/image-name':'${imageName}'))),('$state':(store:appState),meta:(alias:!n,disabled:!f,index:'filebeat-*',key:'kubernetes.labels.tierzero_ymeadows_com/image-tag',negate:!f,params:(query:'${imageTag}'),type:phrase),query:(match_phrase:('kubernetes.labels.tierzero_ymeadows_com/image-tag':'${imageTag}')))),grid:(),hideChart:!f,index:'filebeat-*',interval:auto,query:(language:kuery,query:'kubernetes.labels.serving_knative_dev%2Fservice%20:%20*%20'),sort:!(!('@timestamp',desc)))",
    USERS: '${keycloakUrl}/admin/tier0/console'
} as const;
